import type { Config, Session } from "@openanalyst-ai/sdk/v2/client"
import { getFilename } from "@openanalyst-ai/core/util/path"
import { sessionTitle } from "@/utils/session-title"
import { workspaceKey } from "./helpers"

export type ViewLayerKind = "shared" | "local" | "merged"
export type SidebarThreadFilter = "all" | "active" | "waiting"
export type ThreadStage = "todo" | "doing" | "blocked" | "review" | "done"

type ProjectManagementConfig = NonNullable<NonNullable<Config["view"]>["projectManagement"]>
type ProjectNamingConfig = NonNullable<NonNullable<ProjectManagementConfig["naming"]>["projects"]>[number]
type ThreadNamingConfig = NonNullable<NonNullable<ProjectManagementConfig["naming"]>["threads"]>[number]
type PinnedThreadConfig = NonNullable<
  NonNullable<NonNullable<ProjectManagementConfig["sidebar"]>["pinned"]>["items"]
>[number]

export type LocalProjectViewOverride = {
  name?: string
  code?: string
  updatedAt: number
}

export type LocalThreadViewOverride = {
  title?: string
  stage?: ThreadStage
  priority?: string
  tags?: string[]
  updatedAt: number
}

export type LocalPinnedThread = {
  projectKey: string
  directory: string
  sessionID: string
  pinnedAt: number
}

export type LocalThreadViewState = {
  enabled?: boolean
  kind?: ViewLayerKind
  filter?: SidebarThreadFilter
  pinnedOpen?: boolean
  pinned: Record<string, LocalPinnedThread | undefined>
  projects: Record<string, LocalProjectViewOverride | undefined>
  threads: Record<string, LocalThreadViewOverride | undefined>
}

export type ProjectViewInput = {
  id?: string
  name?: string
  worktree: string
}

export type ProjectView = {
  key: string
  name: string
  code: string
  label: string
}

export type ThreadView = {
  key: string
  title: string
  label: string
  stage: ThreadStage
  stageLabel: string
  priority?: string
  tags: string[]
}

export const defaultThreadViewState = (): LocalThreadViewState => ({
  enabled: undefined,
  kind: undefined,
  filter: "all",
  pinnedOpen: true,
  pinned: {},
  projects: {},
  threads: {},
})

export const defaultStageLabels: Record<ThreadStage, string> = {
  todo: "To do",
  doing: "Doing",
  blocked: "Waiting",
  review: "Review",
  done: "Done",
}

export function threadViewKey(session: Pick<Session, "directory" | "id">) {
  return `${workspaceKey(session.directory)}:${session.id}`
}

export function projectViewKey(project: Pick<ProjectViewInput, "worktree">) {
  return workspaceKey(project.worktree)
}

export function projectManagementConfig(config: Config | undefined) {
  return config?.view?.projectManagement
}

export function viewEnabled(config: ProjectManagementConfig | undefined, local: LocalThreadViewState) {
  return local.enabled ?? config?.enabled ?? true
}

export function layerKind(
  config: ProjectManagementConfig | undefined,
  local: LocalThreadViewState,
  sectionKind?: ViewLayerKind,
): ViewLayerKind {
  return local.kind ?? sectionKind ?? config?.kind ?? "merged"
}

function useShared(kind: ViewLayerKind) {
  return kind === "shared" || kind === "merged"
}

function useLocal(kind: ViewLayerKind) {
  return kind === "local" || kind === "merged"
}

function matchPath(a: string | undefined, b: string | undefined) {
  if (!a || !b) return false
  return workspaceKey(a) === workspaceKey(b)
}

function matchesProject(item: ProjectNamingConfig, project: ProjectViewInput) {
  if (item.match.projectID && item.match.projectID !== project.id) return false
  if (item.match.worktree && !matchPath(item.match.worktree, project.worktree)) return false
  if (item.match.directory && !matchPath(item.match.directory, project.worktree)) return false
  return true
}

function matchesThread(
  item: ThreadNamingConfig | PinnedThreadConfig,
  session: Session,
  project?: ProjectViewInput,
) {
  if (item.match.sessionID && item.match.sessionID !== session.id) return false
  if (item.match.projectID && item.match.projectID !== project?.id) return false
  if (item.match.directory && !matchPath(item.match.directory, session.directory)) return false
  if (item.match.worktree && !matchPath(item.match.worktree, project?.worktree ?? session.directory)) return false
  return true
}

function lastMatch<T>(list: readonly T[] | undefined, predicate: (item: T) => boolean) {
  return (list ?? []).findLast(predicate)
}

function compactCode(value: string) {
  const parts = value
    .split(/[^a-zA-Z0-9]+/)
    .map((part) => part.trim())
    .filter(Boolean)
  const code = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
  return code || value.slice(0, 2).toUpperCase()
}

function renderTemplate(template: string, tokens: Record<string, string>) {
  return Object.entries(tokens).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, value), template)
}

export function resolveProjectView(input: {
  project: ProjectViewInput
  config: ProjectManagementConfig | undefined
  local: LocalThreadViewState
}) {
  const key = projectViewKey(input.project)
  const kind = layerKind(input.config, input.local, input.config?.naming?.kind)
  const shared =
    viewEnabled(input.config, input.local) && useShared(kind) && input.config?.naming?.enabled !== false
      ? lastMatch(input.config?.naming?.projects, (item) => matchesProject(item, input.project))
      : undefined
  const local = viewEnabled(input.config, input.local) && useLocal(kind) ? input.local.projects[key] : undefined
  const fallbackName = input.project.name || getFilename(input.project.worktree)
  const name = local?.name ?? shared?.name ?? fallbackName
  const code = local?.code ?? shared?.code ?? compactCode(name)
  const template =
    viewEnabled(input.config, input.local) && input.config?.naming?.enabled !== false
      ? input.config?.naming?.projectTemplate
      : undefined
  const label = template
    ? renderTemplate(template, {
        "project.name": name,
        "project.code": code,
        "project.basename": getFilename(input.project.worktree),
        "project.worktree": input.project.worktree,
      })
    : name

  return { key, name, code, label } satisfies ProjectView
}

export function resolveThreadView(input: {
  session: Session
  project: ProjectViewInput | undefined
  projectView: ProjectView
  config: ProjectManagementConfig | undefined
  local: LocalThreadViewState
  derivedStage: ThreadStage
}) {
  const key = threadViewKey(input.session)
  const kind = layerKind(input.config, input.local, input.config?.naming?.kind)
  const shared =
    viewEnabled(input.config, input.local) && useShared(kind) && input.config?.naming?.enabled !== false
      ? lastMatch(input.config?.naming?.threads, (item) => matchesThread(item, input.session, input.project))
      : undefined
  const local = viewEnabled(input.config, input.local) && useLocal(kind) ? input.local.threads[key] : undefined
  const title = local?.title ?? shared?.title ?? sessionTitle(input.session.title) ?? "Untitled"
  const stage = local?.stage ?? shared?.stage ?? input.derivedStage
  const stageLabels = { ...defaultStageLabels, ...(input.config?.naming?.stageLabels ?? {}) }
  const stageLabel = stageLabels[stage] ?? stage
  const priority = local?.priority ?? shared?.priority
  const tags = [...(local?.tags ?? shared?.tags ?? [])]
  const template =
    viewEnabled(input.config, input.local) && input.config?.naming?.enabled !== false
      ? input.config?.naming?.threadTemplate
      : undefined
  const label = template
    ? renderTemplate(template, {
        "project.name": input.projectView.name,
        "project.code": input.projectView.code,
        "project.basename": getFilename(input.project?.worktree ?? input.session.directory),
        "project.worktree": input.project?.worktree ?? input.session.directory,
        "thread.title": title,
        "thread.stage": stage,
        "thread.stageLabel": stageLabel,
        "thread.priority": priority ?? "",
        "thread.id": input.session.id,
      })
    : title

  return { key, title, label, stage, stageLabel, priority, tags } satisfies ThreadView
}

export function sidebarTabs(config: ProjectManagementConfig | undefined) {
  const tabs = config?.sidebar?.filters?.tabs
  if (!tabs?.length) return ["all", "active", "waiting"] as SidebarThreadFilter[]
  return tabs
}

export function sidebarDefaultFilter(config: ProjectManagementConfig | undefined) {
  return config?.sidebar?.filters?.default ?? "all"
}

export function pinnedEnabled(config: ProjectManagementConfig | undefined, local: LocalThreadViewState) {
  return viewEnabled(config, local) && config?.sidebar?.pinned?.enabled !== false
}

export function filtersEnabled(config: ProjectManagementConfig | undefined, local: LocalThreadViewState) {
  return viewEnabled(config, local) && config?.sidebar?.filters?.enabled !== false
}

export function isPinnedThread(input: {
  session: Session
  project: ProjectViewInput | undefined
  config: ProjectManagementConfig | undefined
  local: LocalThreadViewState
}) {
  if (!pinnedEnabled(input.config, input.local)) return false
  const kind = layerKind(input.config, input.local, input.config?.sidebar?.pinned?.kind)
  const key = threadViewKey(input.session)
  if (useLocal(kind) && input.local.pinned[key]) return true
  return (
    useShared(kind) &&
    (input.config?.sidebar?.pinned?.items ?? []).some((item) => matchesThread(item, input.session, input.project))
  )
}

export function pinnedSortValue(input: {
  session: Session
  project: ProjectViewInput | undefined
  config: ProjectManagementConfig | undefined
  local: LocalThreadViewState
}) {
  const key = threadViewKey(input.session)
  const local = input.local.pinned[key]?.pinnedAt
  if (local !== undefined) return -local
  const index = (input.config?.sidebar?.pinned?.items ?? []).findIndex((item) =>
    matchesThread(item, input.session, input.project),
  )
  if (index !== -1) return index
  return input.session.time.updated ? -input.session.time.updated : -input.session.time.created
}
